/**
 * Visor ampliado para las referencias de clientes.
 * Usa <dialog> nativo: Escape cierra, el foco queda contenido y vuelve al disparador al cerrar.
 * Navegación anterior/siguiente con botones y flechas del teclado.
 */
class ArturLightbox extends HTMLElement {
  /** @type {AbortController | undefined} */
  #controller;

  /** @type {HTMLButtonElement[]} */
  #triggers = [];

  #index = 0;

  /** @type {HTMLElement | null} */
  #lastTrigger = null;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.#triggers = [...this.querySelectorAll('.artur-refs__open')].filter(
      (el) => el instanceof HTMLButtonElement
    );

    this.addEventListener('click', this.#onClick, { signal });
    this.dialog?.addEventListener('keydown', this.#onKeydown, { signal });
    this.dialog?.addEventListener('close', this.#onClose, { signal });
  }

  disconnectedCallback() {
    this.#controller?.abort();
  }

  /** @returns {HTMLDialogElement | null} */
  get dialog() {
    const id = this.dataset.dialog;
    const el = id ? document.getElementById(id) : null;
    return el instanceof HTMLDialogElement ? el : null;
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const trigger = event.target.closest('.artur-refs__open');
    if (trigger instanceof HTMLButtonElement) {
      this.#open(this.#triggers.indexOf(trigger));
      return;
    }

    const action = event.target.closest('[data-action]');
    if (action instanceof HTMLElement) {
      if (action.dataset.action === 'close') this.dialog?.close();
      if (action.dataset.action === 'prev') this.#show(this.#index - 1);
      if (action.dataset.action === 'next') this.#show(this.#index + 1);
      return;
    }

    // Clic fuera de la imagen (sobre el fondo del visor) cierra
    if (event.target === this.dialog) this.dialog.close();
  };

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    if (event.key === 'ArrowLeft') this.#show(this.#index - 1);
    if (event.key === 'ArrowRight') this.#show(this.#index + 1);
  };

  #onClose = () => {
    document.documentElement.style.removeProperty('overflow');
    this.#lastTrigger?.focus();
  };

  /** @param {number} index */
  #open(index) {
    const dialog = this.dialog;
    if (!dialog || index < 0) return;

    this.#lastTrigger = this.#triggers[index] ?? null;
    dialog.toggleAttribute('data-single', this.#triggers.length < 2);
    this.#show(index);
    document.documentElement.style.overflow = 'hidden';
    dialog.showModal();
  }

  /** @param {number} index */
  #show(index) {
    const count = this.#triggers.length;
    if (!count || !this.dialog) return;

    this.#index = (index + count) % count;
    const trigger = this.#triggers[this.#index];
    if (!trigger) return;

    const caption = this.dialog.querySelector('.artur-lightbox__caption');
    let image = this.dialog.querySelector('.artur-lightbox__image');
    if (!(image instanceof HTMLImageElement)) {
      image = document.createElement('img');
      image.className = 'artur-lightbox__image';
      image.decoding = 'async';
      this.dialog.querySelector('.artur-lightbox__frame')?.prepend(image);
    }

    image.src = trigger.dataset.full ?? '';
    image.alt = trigger.dataset.alt ?? '';
    if (caption) caption.textContent = trigger.dataset.caption ?? '';
  }
}

if (!customElements.get('artur-lightbox')) {
  customElements.define('artur-lightbox', ArturLightbox);
}
